import { FC, useState, useCallback, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLazyQuery } from "@apollo/client";
import { ProductInput } from "@/components/input/ProductInput/ProductInput";
import type { ProductOption } from "@/components/input/ProductInput/types";
import CollectionSelect, {
  type CollectionSelectOption,
} from "@/components/CollectionSelect/CollectionSelect";
import { SIMPLIFIED_PRODUCT_ID_QUERY } from "@/apis/taxonomy/queries";
import type { ProductsQueryPayload } from "@/types/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";

interface CollectionRow {
  id: number;
  option: CollectionSelectOption | null;
  productIds: number[];
  productNames: string[];
  loading: boolean;
}

interface ProductScoringSectionProps {
  productIds: number[];
  onProductsChange: (ids: number[]) => void;
  grouped?: boolean;
}

const COLLECTION_PRODUCTS_TOOLTIP =
  "These products are part of the selected collection. To modify your collection, click 'View My Collections' or go to your library in the settings page.";

/**
 * Shared product selection section for product scoring criteria.
 * Manual products and collection products are tracked separately.
 * Each collection row shows its products as read-only chips.
 */
export const ProductScoringSection: FC<ProductScoringSectionProps> = ({
  productIds,
  onProductsChange,
  grouped,
}) => {
  const [manualProductIds, setManualProductIds] =
    useState<number[]>(productIds);
  const [collectionRows, setCollectionRows] = useState<CollectionRow[]>([]);
  const rowIdCounter = useRef(0);

  const [fetchProducts] = useLazyQuery<ProductsQueryPayload>(
    SIMPLIFIED_PRODUCT_ID_QUERY,
    { context: { clientName: TAXONOMY_CLIENT } }
  );

  // Compute total unique products across manual + all collections
  const totalUniqueCount = useMemo(() => {
    const all = new Set([
      ...manualProductIds,
      ...collectionRows.flatMap((r) => r.productIds),
    ]);
    return all.size;
  }, [manualProductIds, collectionRows]);

  const emitChange = useCallback(
    (manual: number[], collections: CollectionRow[]) => {
      const all = Array.from(
        new Set([...manual, ...collections.flatMap((r) => r.productIds)])
      );
      onProductsChange(all);
    },
    [onProductsChange]
  );

  const handleProductsSelect = useCallback(
    (products: ProductOption[]) => {
      const ids = products.map((p) => Number(p.value));
      setManualProductIds(ids);
      emitChange(ids, collectionRows);
    },
    [collectionRows, emitChange]
  );

  const addCollectionRow = () => {
    rowIdCounter.current += 1;
    setCollectionRows((prev) => [
      ...prev,
      {
        id: rowIdCounter.current,
        option: null,
        productIds: [],
        productNames: [],
        loading: false,
      },
    ]);
  };

  const handleCollectionSelect = useCallback(
    async (index: number, option: CollectionSelectOption) => {
      const ids = (Array.isArray(option.ids) ? option.ids : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      const rowKey = option.value;

      setCollectionRows((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          option,
          productIds: ids,
          productNames: [],
          loading: ids.length > 0,
        };
        emitChange(manualProductIds, updated);
        return updated;
      });

      if (ids.length > 0) {
        try {
          const { data } = await fetchProducts({ variables: { ids } });
          const nameMap = new Map(
            (data?.product?.data ?? []).map((p) => [p.id, p.name])
          );
          setCollectionRows((prev) => {
            const updated = [...prev];
            // Guard: verify the row still corresponds to the same collection
            if (updated[index]?.option?.value !== rowKey) return prev;
            updated[index] = {
              ...updated[index],
              productNames: ids.map((id) => nameMap.get(id) ?? `Product ${id}`),
              loading: false,
            };
            return updated;
          });
        } catch {
          setCollectionRows((prev) => {
            const updated = [...prev];
            if (updated[index]?.option?.value !== rowKey) return prev;
            updated[index] = { ...updated[index], loading: false };
            return updated;
          });
        }
      }
    },
    [manualProductIds, emitChange, fetchProducts]
  );

  const clearCollectionSelection = useCallback(
    (index: number) => {
      setCollectionRows((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          option: null,
          productIds: [],
          productNames: [],
          loading: false,
        };
        emitChange(manualProductIds, updated);
        return updated;
      });
    },
    [manualProductIds, emitChange]
  );

  const removeCollectionRow = useCallback(
    (index: number) => {
      setCollectionRows((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        emitChange(manualProductIds, updated);
        return updated;
      });
    },
    [manualProductIds, emitChange]
  );

  return (
    <>
      {/* Instruction text */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Select your products manually or by your previously created product
        collections, you can add multiple product collections
      </Typography>

      {/* Manual product search */}
      <Box sx={{ mb: 1.5 }}>
        <ProductInput
          onProductsSelect={handleProductsSelect}
          selectedProductIds={manualProductIds}
          placeholder="Search Products"
          grouped={grouped}
          showDoneButton
        />
      </Box>

      {/* View My Collections + info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 1.5,
        }}
      >
        <Tooltip
          title="You will be redirected to your collections within your library."
          placement="top"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: "text.secondary", mr: 0.5 }}
          />
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          href="/settings/collections"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "uppercase", fontSize: 11, fontWeight: 700 }}
        >
          View My Collections
        </Button>
      </Box>

      {/* Collection rows */}
      {collectionRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 1.5,
            maxHeight: 250,
            overflowY: "auto",
          }}
        >
          {collectionRows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1 }}>
                {row.option ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {row.option.label}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => clearCollectionSelection(index)}
                      aria-label="Clear collection"
                      sx={{ ml: 0.5 }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <CollectionSelect
                    placeholder="Search collections"
                    type="product"
                    onSelect={(option) => handleCollectionSelect(index, option)}
                  />
                )}
                {/* Read-only product chips from selected collection */}
                {(row.loading || row.productNames.length > 0) && (
                  <Tooltip title={COLLECTION_PRODUCTS_TOOLTIP} placement="top">
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        bgcolor: "grey.50",
                        cursor: "default",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: "100%", mb: 0.25 }}
                      >
                        Products
                      </Typography>
                      {row.loading ? (
                        <Skeleton
                          variant="rectangular"
                          height={28}
                          width="100%"
                        />
                      ) : (
                        row.productNames.map((name, idx) => (
                          <Chip
                            key={row.productIds[idx] ?? idx}
                            label={name}
                            size="small"
                            disabled
                            sx={{ opacity: 0.8 }}
                          />
                        ))
                      )}
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => removeCollectionRow(index)}
                aria-label="Remove collection"
                sx={{ mt: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Product Collection button */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addCollectionRow}
          sx={{ textTransform: "uppercase", fontSize: 11, fontWeight: 700 }}
        >
          Add Product Collection
        </Button>
      </Box>

      {/* Product count */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, textAlign: "center", mb: 2 }}
      >
        A total of {totalUniqueCount} unique product
        {totalUniqueCount !== 1 ? "s have" : " has"} been selected
      </Typography>
    </>
  );
};
